import { findKey } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import DropdownButton from "PFComponents/dropdown/dropdown_button";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { ApiRoute } from "PFCore/utilities/routes";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./edit_email_templates.less";

const Error = ({ error }) => <div className={css.error}>{error.join(", ")}</div>;

const EditEmailTemplatesPage = ({ emailTemplate, emailTemplateSchemas, id }) => {
  const { t } = useTranslation("admin", { keyPrefix: "emailTemplates" });
  const history = useHistory();
  const growl = useGrowl();

  const [key, setKey] = useState(emailTemplate.key || "");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [subject, setSubject] = useState(emailTemplate.subject || "");
  const [body, setBody] = useState(emailTemplate.body || "");
  const [bodyTip, setBodyTip] = useState("");
  const [callToActionTip, setCallToActionTip] = useState("");
  const [currentSchema, setCurrentSchema] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (key) {
      const selected = findKey(emailTemplateSchemas, (attribute) => attribute.key === key);
      setSelectedIndex(selected);
      setCurrentSchema(emailTemplateSchemas[selected]);
    }
  }, [key]);

  useEffect(() => {
    if (currentSchema.body_default && !id) {
      setBody(currentSchema.body_default);
      setSubject(currentSchema.subject_default);
      setBodyTip(t("availableBodyVariables", { variables: currentSchema.body_variables.join(",") }));
    }
    setCallToActionTip(currentSchema?.extra?.[0]?.value);
  }, [currentSchema]);

  const options = emailTemplateSchemas.map((emailTemplate, index) => ({
    id: index,
    displayElement: <div>{emailTemplate.key}</div>,
    item: emailTemplate.key
  }));

  const publishEmailTemplate = () => {
    const payload = {
      id: id,
      key: key,
      subject: subject,
      body: body
    };

    $.ajax({
      url: ApiRoute(`/api/admin/email_templates/${id ? id : ""}`),
      method: id ? "PUT" : "POST",
      data: payload,
      api_version: 1
    })
      .then(() => {
        growl({ message: t("updateSuccess") });
        history.push("/admin/email_templates");
      })
      .catch((errors) => {
        setErrors(errors?.responseJSON?.errors);
      });
  };

  return (
    <div className={css.wrapper}>
      <h1 className={css.header}>{id ? t("editTemplate", { key }) : t("newTemplate")}</h1>
      <PageBox style={{ padding: 20 }}>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("key")}</div>
          {key && id ? (
            <div className={css.right}>{key}</div>
          ) : (
            <div className={css.right}>
              <DropdownButton
                options={options}
                dropDownStyle={{
                  width: 300,
                  maxWidth: 300,
                  minWidth: 300,
                  left: 0
                }}
                handleChange={setKey}
                popperOptions={{
                  placement: "bottom-start"
                }}
              >
                <div>{options[selectedIndex].displayElement}</div>
              </DropdownButton>
              {errors?.key && <Error error={errors?.key} />}
            </div>
          )}
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("subject")}</div>
          <div className={css.right}>
            <InputFieldSet value={subject} onChange={setSubject} error={errors?.subject} />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("body")}</div>
          <div className={css.right}>
            <InputFieldSet
              inputType="textarea"
              value={body}
              onChange={setBody}
              minHeight={100}
              resize="vertical"
              style={{ width: "100%", paddingLeft: 14, paddingTop: 6 }}
              error={errors?.body}
            />
            {bodyTip && bodyTip}
          </div>
        </div>
        {callToActionTip && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{t("callToAction")}</div>
            <div className={css.right}>{callToActionTip}</div>
          </div>
        )}
        <div className={css.footer}>
          <ButtonLink kind="secondary" href="/admin/email_templates">
            {t("translation:cancel")}
          </ButtonLink>
          <Button
            text={id ? t("translation:update") : t("translation:create")}
            onClick={publishEmailTemplate}
          />
        </div>
      </PageBox>
    </div>
  );
};

EditEmailTemplatesPage.propTypes = {
  emailTemplate: PropTypes.shape({
    key: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string
  }),
  emailTemplateSchemas: PropTypes.arrayOf(
    PropTypes.shape({
      body_default: PropTypes.string,
      subject_default: PropTypes.string,
      key: PropTypes.string
    })
  ),
  id: PropTypes.string
};

Error.propTypes = {
  error: PropTypes.array
};

export default EditEmailTemplatesPage;
